import { HHBox, HHChip } from '@hinge-health/react-component-library';
import { Theme, useMediaQuery } from '@mui/material';
import { SystemStyleObject } from '@mui/system';
import {
  ChipStatus,
  getStatusStyle,
  getToneColors,
  shouldPulseStatus,
  StatusShape,
} from '../utils/status-style.utils';

export type { ChipStatus } from '../utils/status-style.utils';

export const STATUS_CHIP_TEST_ID = 'partner-data-gateway-status-chip';
export const STATUS_CHIP_INDICATOR_TEST_ID =
  'partner-data-gateway-status-chip-indicator';

const INDICATOR_SIZE_PX = 8;
const PULSE_KEYFRAMES = 'pdg-status-chip-pulse';

/**
 * The pulse runs a fixed number of cycles and then rests on the static
 * indicator; the label keeps carrying the status. Keeping the total under
 * 5 s means no pause control is needed (WCAG 2.2.2), on top of the
 * `prefers-reduced-motion` gate in `shouldPulseStatus`.
 */
export const PULSE_DURATION_S = 1.6;
export const PULSE_ITERATIONS = 3;
export const STATUS_CHIP_PULSE_ANIMATION = `${PULSE_KEYFRAMES} ${PULSE_DURATION_S}s ease-in-out ${PULSE_ITERATIONS}`;

/**
 * Geometry per shape. Colour always comes from `currentColor` so the
 * indicator inherits the tone's foreground from the chip.
 */
const SHAPE_STYLES: Record<StatusShape, SystemStyleObject<Theme>> = {
  filled: { borderRadius: '50%', backgroundColor: 'currentColor' },
  hollow: {
    borderRadius: '50%',
    backgroundColor: 'transparent',
    boxShadow: 'inset 0 0 0 2px currentColor',
  },
  half: {
    borderRadius: '50%',
    background: 'linear-gradient(90deg, currentColor 50%, transparent 50%)',
    boxShadow: 'inset 0 0 0 2px currentColor',
  },
  pulse: { borderRadius: '2px', backgroundColor: 'currentColor' },
};

interface StatusIndicatorProps {
  shape: StatusShape;
  isPulsing: boolean;
}

/** Decorative glyph; the chip label carries the meaning for assistive tech. */
const StatusIndicator = ({
  shape,
  isPulsing,
}: StatusIndicatorProps): JSX.Element => (
  <HHBox
    component="span"
    aria-hidden="true"
    data-testid={STATUS_CHIP_INDICATOR_TEST_ID}
    data-shape={shape}
    data-pulsing={isPulsing}
    sx={{
      display: 'inline-block',
      width: INDICATOR_SIZE_PX,
      height: INDICATOR_SIZE_PX,
      flexShrink: 0,
      ...SHAPE_STYLES[shape],
      animation: isPulsing ? STATUS_CHIP_PULSE_ANIMATION : 'none',
      [`@keyframes ${PULSE_KEYFRAMES}`]: {
        '0%, 100%': { opacity: 1 },
        '50%': { opacity: 0.35 },
      },
    }}
  />
);

export interface StatusChipProps {
  status: ChipStatus;
  label: string;
  testId?: string;
}

/**
 * Status pill per the v4 design spec: tone colours from the theme plus a
 * shape indicator, so status is never conveyed by colour alone.
 */
export const StatusChip = ({
  status,
  label,
  testId = STATUS_CHIP_TEST_ID,
}: StatusChipProps): JSX.Element => {
  const prefersReducedMotion = useMediaQuery(
    '(prefers-reduced-motion: reduce)',
  );
  const { tone, shape } = getStatusStyle(status);
  const isPulsing = shouldPulseStatus(status, prefersReducedMotion);

  return (
    <HHChip
      hhVariant="variant-bypass"
      size="small"
      label={label}
      icon={<StatusIndicator shape={shape} isPulsing={isPulsing} />}
      data-testid={testId}
      sx={(theme: Theme): SystemStyleObject<Theme> => {
        const { fg, bg } = getToneColors(theme, tone);

        return {
          color: fg,
          backgroundColor: bg,
          fontWeight: 500,
          '& .MuiChip-icon': { color: fg, marginLeft: 1.25, marginRight: 0 },
          '& .MuiChip-label': { color: fg },
        };
      }}
    />
  );
};
